import { Language } from '@/common/models';
import CreativeFormatOptions from '@/common/models/CreativeFormatOptions';
import CreativeOrientationOptions from '@/common/models/CreativeOrientationOptions';
import Exception from '@/common/models/Exception';
import Geofence from '@/common/models/geo/Geofence';
import GeofenceGroup from '@/common/models/geo/GeofenceGroup';
import Market from '@/common/models/geo/Market';
import IABCategory from '@/common/models/iab-category/IABCategory';
import JourneyAnalysis from '@/common/models/journeyAnalysis/JourneyAnalysis';
import MediaType from '@/common/models/misc-api-models/MediaType';
import Network from '@/common/models/misc-api-models/Network';
import UnitTag from '@/common/models/misc-api-models/UnitTag';
import Site from '@/common/models/site/Site';
import Unit from '@/common/models/unit/Unit';
import UnitPack from '@/common/models/unit/UnitPack';

export default class MapCart {
  constructor() {
    this.cart = [];
    this.exclusions = [];
    this.changedCallback = null;
    this.isInitialized = true;
  }

  get length() {
    return this.cart.length;
  }

  clear() {
    this.remove(this.cart);
  }

  add(collection, isExclusion = false) {
    if (!(collection instanceof Array)) {
      throw new Exception('"MapCart.add" expects an Array as parameter.');
    }

    const notInCart = collection.filter((x) =>
      x.id != null
        ? !this.cart.some((c) => c.id === x.id && x.constructor === c.constructor)
        : this.cart.indexOf(x) === -1,
    );

    if (notInCart.length === 0) {
      return [];
    }

    this.includedUnitIDsCached = null;
    this.includedUnitsCached = null;
    this.excludedUnitIDsCached = null;
    this.excludedUnitsCached = null;

    this.includedSiteIDsCached = null;
    this.includedSitesCached = null;
    this.excludedSiteIDsCached = null;
    this.excludedSitesCached = null;

    this.cart = [...this.cart, ...notInCart];
    if (isExclusion) {
      collection.forEach((x) => this.exclusions.push(x));
    }
    this.fireChange();
    return notInCart;
  }

  remove(collection) {
    if (!(collection instanceof Array)) {
      throw new Exception('"MapCart.remove" expects an Array as parameter.');
    }
    this.includedUnitIDsCached = null;
    this.includedUnitsCached = null;
    this.excludedUnitIDsCached = null;
    this.excludedUnitsCached = null;

    this.includedSiteIDsCached = null;
    this.includedSitesCached = null;
    this.excludedSiteIDsCached = null;
    this.excludedSitesCached = null;

    const cartItems = this.cart.filter((x) => collection.find((c) => c.id === x.id && c.name === x.name) != null);
    this.cart = this.cart.filter((x) => cartItems.indexOf(x) === -1);
    this.exclusions = this.exclusions.filter((x) => cartItems.indexOf(x) === -1);
    this.fireChange();
    return cartItems;
  }

  removeAll(collection) {
    if (collection.length === 0) return [];

    this.includedUnitIDsCached = null;
    this.includedUnitsCached = null;
    this.excludedUnitIDsCached = null;
    this.excludedUnitsCached = null;

    this.includedSiteIDsCached = null;
    this.includedSitesCached = null;
    this.excludedSiteIDsCached = null;
    this.excludedSitesCached = null;

    const type = collection[0].constructor;
    this.cart = this.cart.filter((x) => x.constructor !== type);
    this.exclusions = this.exclusions.filter((x) => x.constructor !== type);
    this.fireChange();
    return collection;
  }

  isUnitInCart(unit) {
    if (unit == null) {
      return false;
    }
    return this.unitsInCart.map((x) => x.id).indexOf(unit.id) > -1;
  }

  isSiteInCart(site) {
    if (site == null) {
      return false;
    }
    return this.sitesInCart.map((x) => x.id).indexOf(site.id) > -1;
  }

  isLocationInCart(location) {
    return this.locationsInCart.map((x) => x.id).indexOf(location.id) > -1;
  }

  isJourneyAnalysisInCart(journeyAnalysis) {
    return this.journeyAnalysesInCart.map((x) => x.id).indexOf(journeyAnalysis.id) > -1;
  }

  isMarketInCart(market) {
    return this.marketsInCart.map((x) => x.id).indexOf(market.id) > -1;
  }

  isNetworkInCart(network) {
    return this.networksInCart.map((x) => x.id).indexOf(network.id) > -1;
  }

  isMediaTypeInCart(mediaType) {
    return this.mediaTypesInCart.map((x) => x.id).indexOf(mediaType.id) > -1;
  }

  isGeofenceGroupInCart(geofenceGroup) {
    return this.geofenceGroupsInCart.map((x) => x.id).indexOf(geofenceGroup.id) > -1;
  }

  fireChange() {
    if (!this.isInitialized) return;

    if (this.fireChangeTimer != null) clearTimeout(this.fireChangeTimer);

    this.fireChangeTimer = setTimeout(() => {
      this.fireChangeTimer = null;
      if (this.changedCallback != null && this.isInitialized) {
        this.changedCallback(this);
      }
    }, 100);
  }

  static isJourneyAnalysis(entity) {
    return entity instanceof JourneyAnalysis;
  }

  static isMarket(entity) {
    return entity instanceof Market;
  }

  static isSite(entity) {
    return entity instanceof Site;
  }

  static isUnit(entity) {
    return entity instanceof Unit;
  }

  static isUnitPack(entity) {
    return entity instanceof UnitPack;
  }

  static isMediaType(entity) {
    return entity instanceof MediaType;
  }

  static isNetwork(entity) {
    return entity instanceof Network;
  }

  static isGeofenceGroup(entity) {
    return entity instanceof GeofenceGroup;
  }

  static isGeofence(entity) {
    return entity instanceof Geofence;
  }

  static isLocation(entity) {
    return entity.level != null;
  }

  static isCountry(entity) {
    return entity.level != null && entity.level === 0;
  }

  static isRegion(entity) {
    return entity.level != null && entity.level === 1;
  }

  static isCity(entity) {
    return entity.level != null && entity.level === 3;
  }

  static isTag(entity) {
    return entity instanceof UnitTag;
  }

  static isLanguage(entity) {
    return entity instanceof Language;
  }

  isExclusion(entity) {
    return this.exclusions.find((x) => x.id === entity.id && x.type === entity.type) != null;
  }

  toggleExclusionFor(entity) {
    if (this.isExclusion(entity)) {
      this.exclusions.splice(this.exclusions.indexOf(this.exclusions.find((x) => x.id === entity.id)), 1);
    } else {
      this.exclusions.push(entity);
    }

    this.fireChange();
  }

  get locationsInCart() {
    return this.cart.filter((x) => MapCart.isLocation(x));
  }

  get includedLocationIDs() {
    return this.locationsInCart.filter((x) => !this.isExclusion(x)).map((x) => x.id);
  }

  get includedLocations() {
    return this.locationsInCart.filter((x) => this.includedLocationIDs.indexOf(x.id) > -1);
  }

  get excludedLocationIDs() {
    return this.locationsInCart.filter((x) => this.isExclusion(x)).map((x) => x.id);
  }

  get excludedLocations() {
    return this.locationsInCart.filter((x) => this.excludedLocationIDs.indexOf(x.id) > -1);
  }

  get regionsInCart() {
    return this.cart.filter((x) => MapCart.isRegion(x));
  }

  get countriesInCart() {
    return this.cart.filter((x) => MapCart.isCountry(x));
  }

  get tagsInCart() {
    return this.cart.filter((x) => MapCart.isTag(x));
  }

  get includedTagIds() {
    return this.tagsInCart.filter((x) => !this.isExclusion(x)).map((x) => x.id);
  }

  get includedTags() {
    return this.tagsInCart.filter((x) => this.includedTagIds.indexOf(x.id) > -1);
  }

  get excludedTags() {
    return this.tagsInCart.filter((x) => this.isExclusion(x));
  }

  //
  // LANGUAGE
  //

  get languagesInCart() {
    return this.cart.filter((x) => MapCart.isLanguage(x));
  }

  get includedLanguageIds() {
    return this.includedLanguages.map((x) => x.id);
  }

  get includedLanguages() {
    return this.languagesInCart.filter((x) => !this.isExclusion(x));
  }

  get excludedLanguageIds() {
    return this.excludedLanguages.map((x) => x.id);
  }

  get excludedLanguages() {
    return this.languagesInCart.filter((x) => this.isExclusion(x));
  }

  //
  // REGION
  //

  get includedRegions() {
    return this.includedLocations.filter((x) => this.regionsInCart.indexOf(x) > -1);
  }

  get excludedRegions() {
    return this.excludedLocations.filter((x) => this.regionsInCart.indexOf(x) > -1);
  }

  get includedCountries() {
    return this.includedLocations.filter((x) => this.countriesInCart.indexOf(x) > -1);
  }

  get excludedCountries() {
    return this.excludedLocations.filter((x) => this.countriesInCart.indexOf(x) > -1);
  }

  //
  // CITY
  //

  get citiesInCart() {
    return this.cart.filter((x) => MapCart.isCity(x));
  }

  get includedCities() {
    return this.includedLocations.filter((x) => this.citiesInCart.indexOf(x) > -1);
  }

  get excludedCities() {
    return this.excludedLocations.filter((x) => this.citiesInCart.indexOf(x) > -1);
  }

  //
  // JOURNEY ANALYSES
  //

  get journeyAnalysesInCart() {
    return this.cart.filter((x) => MapCart.isJourneyAnalysis(x));
  }

  //
  // MARKET
  //

  get marketsInCart() {
    return this.cart.filter((x) => MapCart.isMarket(x));
  }

  get includedMarketIDs() {
    return this.marketsInCart.filter((x) => !this.isExclusion(x)).map((x) => x.id);
  }

  get includedMarkets() {
    return this.marketsInCart.filter((x) => this.includedMarketIDs.indexOf(x.id) > -1);
  }

  get excludedMarketIDs() {
    return this.marketsInCart.filter((x) => this.isExclusion(x)).map((x) => x.id);
  }

  get excludedMarkets() {
    return this.marketsInCart.filter((x) => this.excludedMarketIDs.indexOf(x.id) > -1);
  }

  //
  // NETWORK
  //

  get networksInCart() {
    return this.cart.filter((x) => MapCart.isNetwork(x));
  }

  get includedNetworkIDs() {
    return this.networksInCart.filter((x) => !this.isExclusion(x)).map((x) => x.id);
  }

  get includedNetworks() {
    return this.networksInCart.filter((x) => this.includedNetworkIDs.indexOf(x.id) > -1);
  }

  get excludedNetworkIDs() {
    return this.networksInCart.filter((x) => this.isExclusion(x)).map((x) => x.id);
  }

  get excludedNetworks() {
    return this.networksInCart.filter((x) => this.excludedNetworkIDs.indexOf(x.id) > -1);
  }

  //
  // MEDIA TYPE
  //

  get mediaTypesInCart() {
    return this.cart.filter((x) => MapCart.isMediaType(x));
  }

  get includedMediaTypeIDs() {
    return this.mediaTypesInCart.filter((x) => !this.isExclusion(x)).map((x) => x.id);
  }

  get includedMediaTypes() {
    return this.mediaTypesInCart.filter((x) => this.includedMediaTypeIDs.indexOf(x.id) > -1);
  }

  get excludedMediaTypeIDs() {
    return this.mediaTypesInCart.filter((x) => this.isExclusion(x)).map((x) => x.id);
  }

  get excludedMediaTypes() {
    return this.mediaTypesInCart.filter((x) => this.excludedMediaTypeIDs.indexOf(x.id) > -1);
  }

  //
  // SITE
  //

  get sitesInCart() {
    return this.cart.filter((x) => MapCart.isSite(x));
  }

  get includedSiteIDs() {
    if (this.includedSiteIDsCached == null) {
      this.includedSiteIDsCached = this.sitesInCart.filter((x) => !this.isExclusion(x)).map((x) => x.id);
    }
    return this.includedSiteIDsCached;
  }

  get includedSites() {
    if (this.includedSitesCached == null) {
      this.includedSitesCached = this.sitesInCart.filter((x) => this.includedSiteIDs.indexOf(x.id) > -1);
    }
    return this.includedSitesCached;
  }

  get excludedSiteIDs() {
    if (this.excludedSiteIDsCached == null) {
      this.excludedSiteIDsCached = this.sitesInCart.filter((x) => this.isExclusion(x)).map((x) => x.id);
    }
    return this.excludedSiteIDsCached;
  }

  get excludedSites() {
    if (this.excludedSitesCached == null) {
      this.excludedSitesCached = this.sitesInCart.filter((x) => this.excludedSiteIDs.indexOf(x.id) > -1);
    }
    return this.excludedSitesCached;
  }

  //
  // UNIT PACK
  //

  get unitPacksInCart() {
    return this.cart.filter((x) => MapCart.isUnitPack(x));
  }

  get includedUnitPacks() {
    return this.unitPacksInCart.filter((x) => this.includedUnitPackIDs.indexOf(x.id) > -1);
  }

  get includedUnitPackIDs() {
    return this.unitPacksInCart.filter((x) => !this.isExclusion(x)).map((x) => x.id);
  }

  get excludedUnitPackIDs() {
    return this.unitPacksInCart.filter((x) => this.isExclusion(x)).map((x) => x.id);
  }

  get excludedUnitPacks() {
    return this.unitPacksInCart.filter((x) => this.excludedUnitPackIDs.indexOf(x.id) > -1);
  }

  //
  // UNIT
  //

  get unitsInCart() {
    return this.cart.filter((x) => MapCart.isUnit(x));
  }

  get includedUnitIDs() {
    if (this.includedUnitIDsCached == null) {
      this.includedUnitIDsCached = this.unitsInCart.filter((x) => !this.isExclusion(x)).map((x) => x.id);
    }
    return this.includedUnitIDsCached;
  }

  get includedUnits() {
    if (this.includedUnitsCached == null) {
      this.includedUnitsCached = this.unitsInCart.filter((x) => this.includedUnitIDs.indexOf(x.id) > -1);
    }
    return this.includedUnitsCached;
  }

  get excludedUnitIDs() {
    if (this.excludedUnitIDsCached == null) {
      this.excludedUnitIDsCached = this.unitsInCart.filter((x) => this.isExclusion(x)).map((x) => x.id);
    }
    return this.excludedUnitIDsCached;
  }

  get excludedUnits() {
    if (this.excludedUnitsCached == null) {
      this.excludedUnitsCached = this.unitsInCart.filter((x) => this.excludedUnitIDs.indexOf(x.id) > -1);
    }
    return this.excludedUnitsCached;
  }

  //
  // GEOFENCE
  //

  get geofenceGroupsInCart() {
    return this.cart.filter((x) => MapCart.isGeofenceGroup(x));
  }

  get geofencesInCart() {
    return this.cart.filter((x) => MapCart.isGeofence(x));
  }

  get includedGeofenceGroupIDs() {
    return this.geofenceGroupsInCart.filter((x) => !this.isExclusion(x)).map((x) => x.id);
  }

  get includedGeofenceGroups() {
    return this.geofenceGroupsInCart.filter((x) => this.includedGeofenceGroupIDs.indexOf(x.id) > -1);
  }

  get includedGeofenceIDs() {
    return this.geofencesInCart.filter((x) => !this.isExclusion(x)).map((x) => x.id);
  }

  get includedGeofences() {
    return this.geofencesInCart.filter((x) => this.includedGeofenceIDs.indexOf(x.id) > -1);
  }

  get excludedGeofenceGroupIDs() {
    return this.geofenceGroupsInCart.filter((x) => this.isExclusion(x)).map((x) => x.id);
  }

  get excludedGeofenceGroups() {
    return this.geofenceGroupsInCart.filter((x) => this.excludedGeofenceGroupIDs.indexOf(x.id) > -1);
  }

  get excludedGeofenceIDs() {
    return this.geofencesInCart.filter((x) => this.isExclusion(x)).map((x) => x.id);
  }

  get excludedGeofences() {
    return this.geofencesInCart.filter((x) => this.excludedGeofenceIDs.indexOf(x.id) > -1);
  }

  //
  // CREATIVE FORMAT
  //
  static isCreativeFormats(entity) {
    const defaultCreativeFormats = MapCart.creativeFormaOptions.map((creative) => creative.id);
    return defaultCreativeFormats.includes(entity.id);
  }

  get creativeFormatsInCart() {
    // Cache the creative formats so that we don't have to call that function for all items in the cart.
    // This is a performance optimization.
    MapCart.creativeFormaOptions = CreativeFormatOptions.options();
    return this.cart.filter((x) => MapCart.isCreativeFormats(x));
  }

  get includedCreativeFormatsIDs() {
    return this.creativeFormatsInCart.filter((x) => !this.isExclusion(x)).map((x) => x.id);
  }

  get includedCreativeFormats() {
    return this.creativeFormatsInCart.filter((x) => this.includedCreativeFormatsIDs.indexOf(x.id) > -1);
  }

  get excludedCreativeFormatsIDs() {
    return this.creativeFormatsInCart.filter((x) => this.isExclusion(x)).map((x) => x.id);
  }

  get excludedCreativeFormats() {
    return this.creativeFormatsInCart.filter((x) => this.excludedCreativeFormatsIDs.indexOf(x.id) > -1);
  }

  //
  // ORIENTATION
  //
  static isCreativeOrientation(entity) {
    const defaultCreativeOrientations = MapCart.creativeOrientationOptions.map((creative) => creative.id);
    return defaultCreativeOrientations.includes(entity.id);
  }

  get creativeOrientationsInCart() {
    // Cache the creative formats so that we don't have to call that function for all items in the cart.
    // This is a performance optimization.
    MapCart.creativeOrientationOptions = CreativeOrientationOptions.options();
    return this.cart.filter((x) => MapCart.isCreativeOrientation(x));
  }

  get includedCreativeOrientationIDs() {
    return this.creativeOrientationsInCart.filter((x) => !this.isExclusion(x)).map((x) => x.id);
  }

  get includedCreativeOrientations() {
    return this.creativeOrientationsInCart.filter((x) => this.includedCreativeOrientationIDs.indexOf(x.id) > -1);
  }

  get excludedCreativeOrientationIDs() {
    return this.creativeOrientationsInCart.filter((x) => this.isExclusion(x)).map((x) => x.id);
  }

  get excludedCreativeOrientations() {
    return this.creativeOrientationsInCart.filter((x) => this.excludedCreativeOrientationIDs.indexOf(x.id) > -1);
  }

  //
  // IAB CATEGORY
  //
  static isIabCategory(entity) {
    return entity instanceof IABCategory;
  }

  get iabCategoriesInCart() {
    return this.cart.filter((x) => MapCart.isIabCategory(x));
  }

  get includedIabCategoryIDs() {
    return this.iabCategoriesInCart.filter((x) => !this.isExclusion(x)).map((x) => x.id);
  }

  get includedIabCategories() {
    return this.iabCategoriesInCart.filter((x) => this.includedIabCategoryIDs.indexOf(x.id) > -1);
  }

  get excludedIabCategoryIDs() {
    return this.iabCategoriesInCart.filter((x) => this.isExclusion(x)).map((x) => x.id);
  }

  get excludedIabCategories() {
    return this.iabCategoriesInCart.filter((x) => this.excludedIabCategoryIDs.indexOf(x.id) > -1);
  }
}
